/**
 * client/src/routes.tsx — the route table, the gate, and the one route with two faces.
 *
 * ⚠️ **`loading` is a THIRD state and it renders neither face.** This is the books.karti.ai scar,
 * quoted in its AGENTS.md and repeated here because it is about to be repeated in the same place:
 * rendering the shelf or the front door while `GET /api/v1/session` is still in flight flashes the
 * wrong page at somebody on *every single load*, and on `/` that is the first paint of the site.
 * It is decided once, in `<Gate>` and `<Home>`, and there is no fourth version that guesses.
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { Blank, Landing } from "./App.tsx";
import { A2HSInvite } from "./components/DownloadBook.tsx";
import { useAuth } from "./lib/auth.tsx";
import { Admin } from "./pages/Admin.tsx";
import { Book } from "./pages/Book.tsx";
import { Characters } from "./pages/Characters.tsx";
import { Offline } from "./pages/Offline.tsx";
import { Read } from "./pages/Read.tsx";
import { Room } from "./pages/Room.tsx";
import { Rooms } from "./pages/Rooms.tsx";
import { Share } from "./pages/Share.tsx";
import { Shelf } from "./pages/Shelf.tsx";
import { SignIn } from "./pages/SignIn.tsx";
import { Structure } from "./pages/Structure.tsx";
import { Upload } from "./pages/Upload.tsx";

/**
 * Everything behind a signed-in account. The redirect carries where you were going, so signing in
 * from a deep link lands on the deep link rather than dumping you on the shelf.
 *
 * ⚠️ This gate is a *routing* convenience, never an authorisation. Every one of these screens is
 * enforced again on the server, and byte routes go through `readAccess` refusing 404 (LAW 17). The
 * client's answer is a courtesy; the server's is the authority.
 */
function Gate({ children }: { children: ReactNode }) {
  const { signedIn, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Blank />;
  if (!signedIn) return <Navigate to="/signin" state={{ from: location.pathname + location.search }} replace />;
  return <>{children}</>;
}

/** `/` — the Shelf for whoever is signed in, the front door for whoever is not, nothing while we ask. */
function Home() {
  const { signedIn, loading } = useAuth();
  if (loading) return <Blank />;
  return signedIn ? <Shelf /> : <Landing />;
}

export function AppRoutes() {
  const { signedIn, loading } = useAuth();

  return (
    <>
    {/* §5.8: **for guests, A2HS is prompted on session one**, not the third. It is here rather than
        inside `<AppFrame>` because the screen a guest actually lands on — `/b/:id/read` from a share
        link, or `/r/:code` from a text message — is deliberately OUTSIDE the frame (§5.1's reader is
        a fixed-size non-scrolling box). A guest who installs later gets a home-screen app with its
        own storage and its own cookie jar: a new `gst_` actor, and with it the loss of every reading
        position and every room seat. The third-session prompt fires exactly when they have the most
        to lose. It renders nothing at all once installed, once dismissed, or in any browser that
        offers no install path.

        `float` because the reader is a fixed-size, non-scrolling box measured in pixels by JS
        (§5.2 pitfall 1): a block element above it would take vertical space and re-paginate the
        chapter somebody is in the middle of. It sits over the page, above the safe-area inset, and
        it is dismissible. Signed-out only — the copy is about losing a guest actor, which is not a
        thing that can happen to somebody with an account. */}
    {!loading && !signedIn && <A2HSInvite reason="guest" float />}
    <Routes>
      <Route path="/" element={<Home />} />

      {/* Same three-state rule as <Gate>, in the other direction: never flash the door at somebody
          who already has a key, and never flash the shelf at somebody who does not. */}
      <Route
        path="/signin"
        element={loading ? <Blank /> : signedIn ? <Navigate to="/" replace /> : <SignIn />}
      />

      <Route
        path="/upload"
        element={
          <Gate>
            <Upload />
          </Gate>
        }
      />

      {/* `/b/:bookId` and not `/b/:slug`: for an unlisted book the id IS the capability (§6.3), and a
          `slug` exists only on a public book. Addressing books by slug would make "unlisted" mean
          nothing the moment somebody guessed a title. */}
      <Route
        path="/b/:bookId"
        element={
          <Gate>
            <Book />
          </Gate>
        }
      />

      {/* The reader is NOT gated, and since P4 that is load-bearing rather than forward-looking: it
          is the screen a guest actually gets. `GET /s/:token` redirects a phone that has never seen
          the site straight here, and `readAccess` on the server is what decides — refusing 404 to
          anyone the book is not open to. A client-side sign-in wall here would break the flagship
          flow outright. */}
      <Route path="/b/:bookId/read" element={<Read />} />

      {/* §6.4. An owner's screen — it lists live capabilities in full, which is the entire point of
          it and also the reason it is gated: `<Share>` refuses again with `canEdit` and the server
          refuses a third time with 404. Note there is NO client route at `/s/:token`: that is a
          SERVER route (it mints the guest cookie and the share cookie, LAW 15) and vite.config.ts
          proxies `^/s/` to :8911 for exactly this reason. */}
      <Route
        path="/b/:bookId/share"
        element={
          <Gate>
            <Share />
          </Gate>
        }
      />

      <Route
        path="/b/:bookId/structure"
        element={
          <Gate>
            <Structure />
          </Gate>
        }
      />

      {/* P7d §3. The cast: who is in this book, what they say, and who reads them. A sibling of
          `/structure` in every sense — same gate, same owner-or-god server refusal (404, LAW 17),
          and the same job: showing a human what the machine guessed, WITH the evidence, so they can
          disagree with it. Three real sentences per character is that evidence. */}
      <Route
        path="/b/:bookId/characters"
        element={
          <Gate>
            <Characters />
          </Gate>
        }
      />

      {/* §7.6. `/r/:code` **joins on open**, and like the reader it is deliberately NOT behind
          `<Gate>`: the flagship interaction is somebody sending you a link on a Tuesday night, and a
          sign-in wall between the tap and the book is a wall with the other person sitting behind it
          waiting. Guests are first-class here — §7.6's "a guest can hold the remote" is the point,
          not a concession. `canJoinRoom` on the server is what decides.

          ⚠️ It is a CLIENT route, unlike `/s/:token`. vite.config.ts proxies `^/s/` to :8911 because
          that one mints cookies and redirects (LAW 15); this one is the SPA's, and the proxy table
          says so in as many words. */}
      <Route path="/r/:code" element={<Room />} />

      {/* ⚠️ **NOT wrapped in `<Gate>`, and that is the guest's only door back on Wednesday.**

          §7.6 gives three ways to find a room again — `/r/CODE`, the shelf strip, and this screen —
          and two of the three are user-only, because a signed-out actor gets `<Landing>` rather than
          the shelf. A guest's seat is *durable* (a disconnect never removes a member row) and
          `GET /api/v1/rooms/mine` answers correctly for a `gst_` key: the server knows exactly which
          rooms they are in. `<Gate>` bounced them to /signin anyway, so the only route back was the
          original text message — on a product whose invited tier is deliberately narrow and whose
          guests are the people it is for.

          Safe by construction: `/rooms/mine` re-runs `readAccessForActor` per row and returns
          `{rooms: []}` for an unknown actor, so an ungated screen shows a stranger nothing. */}
      <Route path="/rooms" element={<Rooms />} />

      {/* §4.12's four tabs. NOT wrapped in `<Gate>` — `<Admin>` decides for itself with `isGod`
          (LAW 13) and redirects home, so a signed-in non-god gets the same nothing a stranger gets.
          A `<Gate>` here would bounce a stranger to /signin, which tells them the page is real. */}
      <Route path="/admin" element={<Admin />} />

      {/* §5.8's offline shelf: what this browser is holding, what is waiting to be sent, and the
          two limits (Safari's seven days, a standalone PWA's separate storage) stated rather than
          hidden. NOT wrapped in `<Gate>` — it is built entirely from IndexedDB and needs no server
          at all, which is the point, and bouncing somebody with no signal to a sign-in form that
          cannot answer is the blank page this phase exists to delete. */}
      <Route path="/offline" element={<Offline />} />

      {/* Anything else goes home rather than to a blank page. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
